import { Phone, MessageCircle, X, Globe } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * CrisisCard — appears when the user expresses suicidal/self-harm intent.
 * Soft, non-alarming, immediate access to real human help.
 * Includes helplines for US + India by default.
 */
export default function CrisisCard({ visible, onDismiss }) {
    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.4 }}
                    className="mb-5 bg-gradient-to-br from-[#EAF2FB] to-[#D6E5F3] border border-terracotta/30 rounded-3xl p-6 shadow-soft relative"
                    data-testid="crisis-card"
                >
                    <button
                        onClick={onDismiss}
                        data-testid="crisis-card-dismiss"
                        className="absolute top-4 right-4 text-inkSoft hover:text-ink"
                        aria-label="Dismiss"
                    >
                        <X className="w-4 h-4" />
                    </button>
                    <p className="font-heading text-lg text-ink mb-1 tracking-tight">
                        I'm here, and I want a real human here too.
                    </p>
                    <p className="font-body text-inkSoft text-sm leading-relaxed mb-5 pr-6">
                        Please reach out to someone trained to be with you right now. You don't have to carry this alone.
                    </p>

                    {/* US helplines */}
                    <p className="text-xs uppercase tracking-widest text-inkSoft mb-2 font-body">United States</p>
                    <div className="grid sm:grid-cols-2 gap-3 mb-5">
                        <a
                            href="tel:988"
                            data-testid="crisis-call-988"
                            className="flex items-center gap-3 bg-terracotta text-bone rounded-2xl px-5 py-3 hover:bg-terracottaHover transition-colors font-body"
                        >
                            <Phone className="w-4 h-4" />
                            <div>
                                <div className="text-sm font-medium">call 988</div>
                                <div className="text-xs opacity-80">suicide & crisis lifeline</div>
                            </div>
                        </a>
                        <a
                            href="sms:741741?&body=HOME"
                            data-testid="crisis-text"
                            className="flex items-center gap-3 bg-white border border-clayHover text-ink rounded-2xl px-5 py-3 hover:bg-clay transition-colors font-body"
                        >
                            <MessageCircle className="w-4 h-4 text-terracotta" />
                            <div>
                                <div className="text-sm font-medium">text HOME to 741741</div>
                                <div className="text-xs text-inkSoft">crisis text line</div>
                            </div>
                        </a>
                    </div>

                    {/* India helplines */}
                    <p className="text-xs uppercase tracking-widest text-inkSoft mb-2 font-body">India</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <a
                            href="tel:18005990019"
                            data-testid="crisis-call-kiran"
                            className="flex items-center gap-3 bg-white border border-clayHover text-ink rounded-2xl px-5 py-3 hover:bg-clay transition-colors font-body"
                        >
                            <Phone className="w-4 h-4 text-terracotta" />
                            <div>
                                <div className="text-sm font-medium">call 1800-599-0019</div>
                                <div className="text-xs text-inkSoft">KIRAN · 24/7 · free</div>
                            </div>
                        </a>
                        <a
                            href="tel:+919152987821"
                            data-testid="crisis-call-icall"
                            className="flex items-center gap-3 bg-white border border-clayHover text-ink rounded-2xl px-5 py-3 hover:bg-clay transition-colors font-body"
                        >
                            <Phone className="w-4 h-4 text-terracotta" />
                            <div>
                                <div className="text-sm font-medium">call +91 9152987821</div>
                                <div className="text-xs text-inkSoft">iCall · Mon–Sat · 8a–10p</div>
                            </div>
                        </a>
                    </div>

                    <div className="mt-5 flex items-start gap-2 text-xs text-inkSoft font-body">
                        <Globe className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        <p>
                            elsewhere?{" "}
                            <a
                                href="https://findahelpline.com"
                                target="_blank"
                                rel="noreferrer"
                                data-testid="crisis-link-findahelpline"
                                className="text-terracotta hover:underline"
                            >
                                findahelpline.com
                            </a>{" "}
                            lists real humans by country. pax will keep listening, but a real human matters more right now.
                        </p>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
